import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';

import { createTheme, CssBaseline, ThemeProvider } from '@mui/material';
import { theme } from '@sprout/mui';

import Login from './features/Login';
import { initPromise } from './i18n';

import '@sprout/css';
import '@sprout/css/dist/index.css';
import '@sprout/css/dist/utilities.css';
import '@sprout/css/dist/templates.css';
import './styles/global/index.css';
import './styles/colors.css';
import './index.css';
import './utils/polyfills';

function StepCircleIcon({ active, completed, icon, className }) {
  const isActive = active || completed;

  const bgColor = isActive ? 'var(--brand-leaf-green)' : 'var(--neutral-200)';

  const textColor = isActive ? 'var(--brand-white)' : 'var(--neutral-600)';

  return (
    <svg width="24" height="24" viewBox="0 0 24 24" className={className}>
      {/* Background circle */}
      <circle cx="12" cy="12" r="12" fill={bgColor} />

      {/* Completed checkmark */}
      {completed ? (
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d={`
            M16.911 8.25664C17.0752 8.42082 17.1674 8.64348 
            17.1674 8.87564C17.1674 9.10779 17.0752 9.33045 16.911 9.49464L10.3486 
             16.057C10.2619 16.1438 10.159 16.2126 10.0456 16.2595C9.93232 16.3065 9.81087 
             16.3306 9.68821 16.3306C9.56555 16.3306 9.44409 16.3065 9.33077 16.2595C9.21745 16.2126 9.11449 16.1438 9.02776 
             16.057L5.76729 12.7971C5.68367 
             12.7164 5.61697 12.6198 5.57108 12.5129C5.5252 12.4061 5.50104 12.2912 5.50003 12.175C5.49902 12.0587 5.52118 
             11.9434 5.5652 11.8358C5.60922 11.7282 5.67423 11.6305 5.75644 11.5483C5.83864 11.4661 5.9364 11.4011 
             6.044 11.357C6.1516 11.313 6.26689 11.2909 6.38314 11.2919C6.49939 11.2929 6.61428 11.317 6.7211 
             11.3629C6.82791 11.4088 6.92452 11.4755 7.00529 11.5591L9.68791 14.2418L15.6725 8.25664C15.7538 
             8.17528 15.8503 8.11073 15.9566 8.0667C16.0628 8.02266 16.1767 8 16.2917 8C16.4068 8 16.5207 
             8.02266 16.6269 8.0667C16.7332 8.11073 16.8297 8.17528 16.911 8.25664Z`}
          fill="white"
          stroke="white"
          strokeWidth="0.5"
        />
      ) : (
        <text
          x="12"
          y="12"
          textAnchor="middle"
          dominantBaseline="middle"
          fontFamily="Helvetica Now For Cargill, Helvetica, Arial, sans-serif"
          fontSize="12"
          fontWeight="700"
          fill={textColor}
          style={{
            fontFeatureSettings: "'liga' off, 'clig' off"
          }}
        >
          {icon}
        </text>
      )}
    </svg>
  );
}

function StepCircleIconWrapper(stepIconProps) {
  return <StepCircleIcon {...stepIconProps} />;
}
const originalMatchMedia = window.matchMedia;
window.matchMedia = query => {
  if (query === '(prefers-color-scheme: dark)') {
    return {
      matches: false,
      media: query,
      onchange: null,
      addListener: () => {},
      removeListener: () => {},
      addEventListener: () => {},
      removeEventListener: () => {},
      dispatchEvent: () => false
    };
  }
  return originalMatchMedia(query);
};

const customTheme = {
  ...theme,
  breakpoints: {
    ...theme.breakpoints,
    values: {
      xs: 0, // mobile small
      sm: 480, // mobile large
      md: 769, // tablet
      lg: 1024, // laptop
      xl: 1440 // desktop
    }
  },
  components: {
    ...theme.components,
    MuiSelect: {
      ...theme.components.MuiSelect,
      styleOverrides: {
        ...theme.components.MuiSelect.styleOverrides,
        root: ({ theme }) =>
          theme.unstable_sx({
            '& .select__placeholder': { color: 'var(--neutral-500)' }
          })
      }
    },
    MuiRadio: {
      ...theme.components.MuiRadio,
      styleOverrides: {
        ...theme.components.MuiRadio?.styleOverrides,
        root: ({ theme }) =>
          theme.unstable_sx({
            '&.Mui-focusVisible': {
              '& .MuiSvgIcon-root': {
                borderRadius: '1rem',
                outline: `2px solid ${theme.palette.background.interactive}`
              }
            }
          })
      }
    },
    MuiInputLabel: {
      ...theme.components.MuiInputLabel,
      defaultProps: {
        ...(theme.components.MuiInputLabel?.defaultProps ?? {}),
        shrink: true
      },
      styleOverrides: {
        ...theme.components.MuiInputLabel.styleOverrides,
        root: ({ theme }) =>
          theme.unstable_sx({
            '& .MuiInputLabel-asterisk': {
              color: 'var(--emphasis-ruby-red-500)',
              fontSize: '12px',
              fontWeight: 700,
              lineHeight: '18px'
            },
            fontWeight: 700,
            fontSize: '0.75rem',
            lineHeight: '18px',
            color: 'var(--neutral-1000)',
            '&.Mui-focused': {
              color: 'var(--neutral-1000)'
            },
            '&.Mui-error': {
              color: 'var(--neutral-1000)'
            },
            '&.Mui-disabled': {
              color: 'var(--neutral-1000)'
            },
            transform: 'translate(2px, -6px) scale(1)',
            paddingBottom: '8px'
          })
      }
    },
    MuiAccordion: {
      ...theme.components.MuiAccordion,
      styleOverrides: {
        ...theme.components.MuiAccordion.styleOverrides,
        root: ({ theme }) =>
          theme.unstable_sx({
            borderRadius: 'var(--corner-radius-base) !important',
            border: '1px solid var(--border-primary)',
            background: 'var(--brand-white)',
            padding: 'var(--padding-lg)'
          })
      }
    },
    MuiAccordionSummary: {
      ...theme.components.MuiAccordionSummary,
      styleOverrides: {
        root: ({ theme }) =>
          theme.unstable_sx({
            padding: '0',
            minHeight: 'unset !important',
            '& .MuiAccordionSummary-content': { margin: '0 !important' }
          })
      }
    },
    MuiAccordionDetails: {
      ...theme.components.MuiAccordionDetails,
      styleOverrides: {
        root: ({ theme }) => theme.unstable_sx({ padding: '0' })
      }
    },
    MuiStep: {
      styleOverrides: {
        root: ({ theme }) =>
          theme.unstable_sx({
            '&.MuiStep-horizontal': {
              '& .MuiStepLabel-root': {
                flexDirection: 'column'
              },
              '& .MuiStepLabel-label.MuiStepLabel-alternativeLabel': {
                marginTop: 0
              },
              '& .MuiStepLabel-iconContainer': {
                paddingRight: 0,
                '& .MuiSvgIcon-root': {
                  width: '100%'
                }
              }
            },
            '&.MuiStep-vertical': {
              '& .MuiStepLabel-label.MuiStepLabel-alternativeLabel': {
                marginTop: 0
              },
              '& .MuiStepLabel-iconContainer': {
                '& .MuiSvgIcon-root': {
                  height: '42px'
                }
              }
            }
          })
      }
    },
    MuiStepLabel: {
      ...theme.components.MuiStepLabel,
      defaultProps: {
        ...(theme.components.MuiStepLabel?.defaultProps ?? {}),
        slots: {
          ...(theme.components.MuiStepLabel?.defaultProps?.slots ?? {}),
          stepIcon: StepCircleIconWrapper
        }
      },
      styleOverrides: {
        root: ({ theme }) =>
          theme.unstable_sx({
            '& .MuiStepLabel-label': {
              fontWeight: 700,
              fontSize: '1rem',
              color: 'text.primary',
              '&.Mui-completed': {
                fontWeight: 700,
                fontSize: '1rem',
                color: 'text.primary'
              },
              '&.Mui-active': {
                fontWeight: 700,
                fontSize: '1rem',
                color: 'text.primary'
              }
            },
            '&.Mui-active .MuiStepConnector-line': {
              borderColor: 'var(--brand-leaf-green)'
            },
            '&.Mui-completed .MuiStepConnector-line': {
              borderColor: 'var(--brand-leaf-green)'
            },
            '& .MuiStepLabel-iconContainer': {
              paddingRight: 0,
              paddingLeft: 0,
              position: 'relative',
              minWidyh: 'unset',
              zIndex: 1
            },
            '& .MuiStepLabel-labelContainer': {
              width: '100%',
              marginTop: 'var(--spacing-sm)'
            }
          })
      }
    },
    MuiStepConnector: {
      ...theme.components.MuiStepConnector,
      styleOverrides: {
        root: ({ theme }) =>
          theme.unstable_sx({
            '&.Mui-active .MuiStepConnector-line': {
              borderColor: 'var(--brand-leaf-green)'
            },
            '&.Mui-completed .MuiStepConnector-line': {
              borderColor: 'var(--brand-leaf-green)'
            }
          }),
        line: {
          borderColor: 'var(--neutral-300)',
          borderTopWidth: 3,
          borderRadius: 'var(--corner-radius-base)'
        }
      }
    },
    MuiTextField: {
      ...theme.components.MuiTextField,
      styleOverrides: {
        ...theme.components.MuiTextField.styleOverrides,
        root: ({ theme }) =>
          theme.unstable_sx({
            '& .MuiInputBase-root': { backgroundColor: 'var(--brand-white)' }
          })
      }
    },
    MuiInputBase: {
      ...theme.components.MuiInputBase,
      styleOverrides: {
        ...theme.components.MuiInputBase.styleOverrides,
        root: ({ theme }) => theme.unstable_sx({ backgroundColor: 'var(--brand-white)' }),
        input: ({ theme }) =>
          theme.unstable_sx({
            ...theme.typography.body1,
            borderRadius: '0.5rem'
          })
      },
      variants: [
        {
          props: { disabled: true },
          style: ({ theme }) =>
            theme.unstable_sx({
              '&.Mui-disabled': {
                border: 'none',
                color: 'text.disabled',
                backgroundColor: 'background.surfaceDisabled'
              }
            })
        }
      ]
    },
    MuiButton: {
      ...theme.components.MuiButton,
      styleOverrides: {
        ...theme.components.MuiButton.styleOverrides,
        contained: {
          ...theme.components.MuiButton.styleOverrides.contained,
          '&.Mui-disabled': {
            background: 'var(--neutral-100) !important',
            color: 'var(--neutral-400) !important'
          }
        }
      }
    },
    MuiIconButton: {
      ...theme.components.MuiIconButton,
      styleOverrides: {
        root: ({ theme }) =>
          theme.unstable_sx({
            fontSize: 'var(--spacing-lg)',
            '&:hover': { backgroundColor: 'var(--brand-green-100)' },
            '&.Mui-disabled': {
              '&::before': { color: 'var(--neutral-400) !important' }
            }
          })
      }
    }
  }
};
const sproutTheme = createTheme(customTheme);
// export const sproutTheme = createTheme({
//   colorSchemes,
//   typography,
//   components: {
//     ...FoundationComponents,
//     ...FormComponents,
//     ...AncillaryComponents,
//   },
// });

const rootElement = document.getElementById('root');
const root = createRoot(rootElement);

const renderApp = () => {
  root.render(
    <ThemeProvider theme={sproutTheme}>
      <CssBaseline />
      <BrowserRouter basename="/food/onboarding">
        <Login />
      </BrowserRouter>
    </ThemeProvider>
  );
};

// i18n is configured with `useSuspense: false`, so components can render
// before translations finish loading. Render immediately and let i18next
renderApp();

initPromise.catch(error => {
  console.error('Failed to initialize i18n:', error);
});
